/**
 * Scrolling in the stacked layout (20_global.sass "Sections and the roll-over").
 *
 * A section that takes part in the roll-over is sticky and reports its pinned
 * position, so an element's in-flow position is derived from the siblings
 * above its section. Such a section stops moving once its bottom reaches the
 * viewport bottom — the next section slides over it — so an anchor scroll
 * never goes further than that: it would only push the target under the
 * footer. A section that does not stick has no such limit (freezeScroll →
 * Infinity), and every rule below falls back to the plain position.
 */
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '])

/** A key that scrolls the page: the visitor taking over from an animation or a held state. */
export const isScrollKey = (event) => SCROLL_KEYS.has(event.key)

/**
 * In-flow top of an element, ignoring the sticky displacement of its section.
 * Margins count: a reference slide overlaps what follows it by its lift, a
 * negative margin-bottom (85_blocks.sass).
 */
export const flowTop = (element) => {
  const section = element.closest('[data-section]')
  if (!section?.parentElement) return element.getBoundingClientRect().top + window.scrollY
  const parent = section.parentElement
  let top = parent.getBoundingClientRect().top + window.scrollY
  for (const child of parent.children) {
    if (child === section) break
    const style = getComputedStyle(child)
    top += child.offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom)
  }
  return top + element.getBoundingClientRect().top - section.getBoundingClientRect().top
}

/** Scroll position at which a stacked section stops moving (fully read); Infinity if it does not stick. */
export const freezeScroll = (section) => {
  if (!section || getComputedStyle(section).position !== 'sticky') return Infinity
  return flowTop(section) + Math.max(0, section.offsetHeight - window.innerHeight)
}

/** Where a jump to an anchor target lands: its scroll-margin-top above it, never beyond the freeze. */
export const landing = (target) => {
  const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0
  const limit = freezeScroll(target.closest('[data-section]'))
  return Math.max(0, Math.round(Math.min(flowTop(target) - margin, limit)))
}

/** Anchor targets a module scrolls to itself (sectionsNav.js) — header.js leaves them alone. */
export const claimed = new WeakSet()

let generation = 0

/**
 * Scroll the window to `top`: animated unless `instant`, motion is reduced or
 * the distance is tiny. A wheel, touch or scroll key hands control back to the
 * visitor and a newer call supersedes an animation still travelling. Resolves
 * true once the position is reached, false when the scroll was given up.
 */
export const scrollToY = (top, { instant = false } = {}) =>
  new Promise((resolve) => {
    generation += 1
    const own = generation
    const from = window.scrollY
    const distance = top - from
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (instant || reduceMotion || Math.abs(distance) < 2) {
      window.scrollTo({ top, behavior: 'instant' })
      resolve(true)
      return
    }

    const duration = Math.min(1200, 500 + Math.abs(distance) / 6)
    const start = performance.now()
    let cancelled = false
    const cancel = () => {
      cancelled = true
    }
    const cancelOnKey = (event) => {
      if (isScrollKey(event)) cancel()
    }
    window.addEventListener('wheel', cancel, { passive: true })
    window.addEventListener('touchstart', cancel, { passive: true })
    window.addEventListener('keydown', cancelOnKey)
    const finish = (arrived) => {
      window.removeEventListener('wheel', cancel)
      window.removeEventListener('touchstart', cancel)
      window.removeEventListener('keydown', cancelOnKey)
      resolve(arrived)
    }
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

    const frame = (now) => {
      if (cancelled || own !== generation) {
        finish(false)
        return
      }
      const t = Math.min(1, (now - start) / duration)
      window.scrollTo({ top: from + distance * ease(t), behavior: 'instant' })
      if (t < 1) window.requestAnimationFrame(frame)
      else finish(true)
    }
    window.requestAnimationFrame(frame)
  })

/** Jump to an anchor target (see `landing`). */
export const scrollToTarget = (target, options) => scrollToY(landing(target), options)
